'use client';

import { useCallback, useState } from 'react';
import {
	BigNum,
	BN,
	SpotMarketConfig,
	SwapQuote,
	UnifiedSwapClient,
} from '@velocity-exchange/sdk';
import { PublicKey, TransactionInstruction } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import bs58 from 'bs58';
import {
	createSwiftAccountIxIfNotExists,
	getTokenAddressForDepositAndWithdraw,
	MAIN_POOL_ID,
	MarketId,
} from '@velocity-exchange/common';

import { useQueryClient } from '@tanstack/react-query';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { isSignedMsgUserOrdersAccountInitializedQueryKey } from 'src/hooks/signedMsgOrders/useIsSignedMsgUserOrdersAccountInitialized';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import useDepositInputStore from 'src/stores/DepositInputStore/useDepositInputStore';
import { useTokenAccountsStore } from 'src/stores/tokenAccounts/useTokenAccountsStore';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import { useAccountInitialization } from 'src/hooks/velocityActions/user/useAccountInitialization';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { updateAfterTx } from 'src/stores/VelocityStore/helpers';
import { getUserIdForAccountInitialization } from 'src/utils/userAccounts';
import { getReferrerInfo } from 'src/utils/referral';
import {
	getMinOutBigNum,
	getReceivedTokenAmount,
} from 'src/utils/swapAndDeposit';
import { buildSwapTxAtTightestSlippage } from './helpers/buildSwapAndDepositSwapTx';
import { DEFAULT_ACCOUNT_NAMES_BY_POOL_ID } from 'src/constants/constants';
import {
	SWAP_AND_DEPOSIT_SLIPPAGE_LADDER_BPS,
	USDC_MAINNET_MINT,
} from 'src/constants/swapAndDeposit';

export type SwapAndDepositResult = 'deposited' | 'swap_only' | 'failed';

// Concurrent order slots allocated for a referred user's RevenueShareEscrow at
// signup. Mirrors common's createUserAndDepositCollateralBaseIxs default
// (DEFAULT_REFERRAL_ESCROW_NUM_ORDERS), which is module-private and not exported.
const DEFAULT_REFERRAL_ESCROW_NUM_ORDERS = 16;

export interface SwapAndDepositArgs {
	/**
	 * The quote on screen, at the tightest rung of the slippage ladder. Execution
	 * re-quotes at a wider tolerance if that rung won't simulate, so this is what
	 * the user approved, not necessarily what gets swapped.
	 */
	quote: SwapQuote;
	/** Must be the client that produced `quote`. */
	quoteClient: UnifiedSwapClient;
	usdtMarket: SpotMarketConfig;
	/** '' when the deposit targets a brand-new account. */
	targetAccountKey: string;
	poolId: number;
	/**
	 * Floor the deposited amount must clear (e.g. the minimum initial deposit).
	 * Widening slippage lowers minOut, so a gate the modal passed at the tightest
	 * rung can stop holding — abort rather than swap into a failing deposit.
	 */
	minAcceptableOut?: BigNum;
	/**
	 * Called the moment a newly created account succeeds, before the trailing
	 * account-subscription settling sleeps. Pass a close handler so the modal
	 * doesn't sit on a loading button for those few seconds.
	 */
	onAccountCreated?: () => void;
}

const TOAST_ID = 'swap-and-deposit';
const INIT_TOAST_ID = 'swap-and-deposit-account-init';

export const useSwapAndDeposit = () => {
	const velocityClient = useVelocityClient();
	const connection = useVelocityStore((s) => s.connection.current);
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const isGeoblocked = useVelocityStore((s) => s.isGeoblocked);
	const countryCode = useVelocityStore((s) => s.countryCode);
	const referrerParam = useVelocityStore((s) => s.referrerParam);
	const accountsGet = useVelocityAccountsStore((s) => s.get);
	const setDepositInputStore = useDepositInputStore((s) => s.set);
	const setTokenAccountsStore = useTokenAccountsStore((s) => s.set);
	const getTxParams = useGetTxParams();
	const queryClient = useQueryClient();
	const { runAccountInitializationTxAndHandling } = useAccountInitialization();
	const { captureEvent } = usePostHogCapture();
	const [isSubmitting, setIsSubmitting] = useState(false);

	/**
	 * Writes one token account into the token-accounts store, which is otherwise
	 * only synced once per wallet (see stores/tokenAccounts/README.md). Needed
	 * after a swap: a wallet with no pre-existing ATA for the bought token has no
	 * store entry at all, so anything reading its balance sees 0.
	 */
	const syncTokenAccountBalance = useCallback(
		async (mint: PublicKey, tokenAccount: PublicKey, fallback: BigNum) => {
			let parsedBalance = fallback.toNum();

			try {
				if (!connection) throw new Error('No connection');
				const { value } = await connection.getTokenAccountBalance(tokenAccount);
				parsedBalance = value.uiAmount ?? parsedBalance;
			} catch (err) {
				// Fall back to the amount we know just landed there; the balance
				// subscription in useSPLTokenBalance corrects it once it emits.
				console.error('Failed to read swapped token account balance', err);
			}

			setTokenAccountsStore((s) => {
				s.tokenAccounts[mint.toBase58()] = {
					pubkey: tokenAccount,
					mint,
					programId: TOKEN_PROGRAM_ID,
					parsedBalance,
				};
			});
		},
		[connection, setTokenAccountsStore]
	);

	const swapAndDeposit = useCallback(
		async ({
			quote,
			quoteClient,
			usdtMarket,
			targetAccountKey,
			poolId,
			minAcceptableOut,
			onAccountCreated,
		}: SwapAndDepositArgs): Promise<SwapAndDepositResult> => {
			if (!velocityClient || !connection || !walletAdapter?.publicKey) {
				return 'failed';
			}

			if (isGeoblocked && countryCode !== 'US') {
				notify({
					type: 'error',
					message: 'Geoblocked',
					description:
						'You are not allowed to use Velocity from a restricted territory',
					lengthMs: 5000,
				});
				return 'failed';
			}

			const authority = walletAdapter.publicKey;

			const usdtSpotMarketAccount = velocityClient.getSpotMarketAccount(
				usdtMarket.marketIndex
			);
			if (!usdtSpotMarketAccount) {
				notify({
					type: 'error',
					message: 'Swap & Deposit unavailable',
					description: 'The USDT market is still loading. Please try again.',
					lengthMs: 5000,
				});
				return 'failed';
			}

			const inAmountBigNum = BigNum.from(
				quote.inAmount,
				usdtMarket.precisionExp // USDC and USDT are both 6dp
			);

			/**
			 * What the deposit leg commits to. Starts from the displayed quote and
			 * is replaced by the executed quote's once the slippage ladder settles
			 * — nothing below can be reached before that happens.
			 */
			let minOut = getMinOutBigNum(quote, usdtMarket.precisionExp);

			/** True when `signature` is on chain and did not error. */
			const swapLanded = async (signature: string) => {
				try {
					const { value } = await connection.getSignatureStatus(signature, {
						searchTransactionHistory: true,
					});
					return !!value && !value.err;
				} catch (statusErr) {
					console.error('Failed to read swap signature status', statusErr);
					return false;
				}
			};

			/**
			 * The swap landed but the deposit didn't. The USDT is safe in the
			 * wallet — hand the user the normal deposit flow, prefilled with the
			 * amount actually received.
			 */
			const degradeToSwapOnly = async (
				swapTxSig: string
			): Promise<SwapAndDepositResult> => {
				captureEvent('swap_and_deposit_deposit_leg_failed', {
					swap_tx_sig: swapTxSig,
				});

				let received: BigNum | null = null;
				try {
					const confirmedTx = await connection.getTransaction(swapTxSig, {
						maxSupportedTransactionVersion: 1,
						commitment: 'finalized',
					});
					received = getReceivedTokenAmount({
						meta: confirmedTx?.meta ?? null,
						mint: usdtMarket.mint.toBase58(),
						owner: authority.toBase58(),
						precisionExp: usdtMarket.precisionExp,
					});
				} catch (parseErr) {
					console.error('Failed to parse swap tx meta', parseErr);
				}

				const prefillAmount = received ?? minOut;

				// The deposit modal clamps its input to the wallet balance it reads
				// from the token-accounts store, which is only synced once per
				// wallet — pre-swap, or (with no USDT ATA before the swap) not at
				// all. Sync this account first or the prefill gets clamped away.
				await syncTokenAccountBalance(
					usdtMarket.mint,
					await getTokenAddressForDepositAndWithdraw(
						usdtSpotMarketAccount,
						authority
					),
					prefillAmount
				);

				// Prefill BEFORE navigating: the deposit modal re-mounts and
				// reads these on mount; persistInputAmount stops its
				// market-change effect from clearing the amount.
				setDepositInputStore((s) => {
					s.selectedSpotMarket = usdtMarket;
					s.depositInputAmount = prefillAmount.print();
					s.persistInputAmount = true;
				});

				notify({
					type: 'warning',
					message: 'Swap complete — deposit not finished',
					description: `${prefillAmount.prettyPrint()} USDT is in your wallet. Confirm the deposit to finish.`,
					id: TOAST_ID,
					lengthMs: 8000,
				});

				return 'swap_only';
			};

			setIsSubmitting(true);

			/** Set once the swap is signed, so a thrown send can still be checked. */
			let swapTxSig: string | null = null;

			try {
				captureEvent('swap_and_deposit_submitted', {
					usdc_amount: inAmountBigNum.toNum(),
					usdt_min_out: minOut.toNum(),
					new_account: !targetAccountKey,
				});

				notify({
					type: 'awaiting',
					message: `Swapping ${inAmountBigNum.prettyPrint()} USDC`,
					description: 'Finding the tightest route…',
					id: TOAST_ID,
					showUntilCancelled: true,
				});

				// ---- Build tx 1: the standalone Jupiter swap ----
				// Quoted and built for the signing wallet, not the target account's
				// authority — a delegated account's authority is a different wallet
				// whose token accounts this signer can't move funds through. It's
				// also the wallet the quote was requested for: Jupiter v2 binds a
				// route to its taker, and getSwapTransaction rejects any other
				// signer.
				const { attempt, error: swapBuildError } =
					await buildSwapTxAtTightestSlippage({
						connection,
						quoteClient,
						approvedQuote: quote,
						inputMint: USDC_MAINNET_MINT,
						outputMint: usdtMarket.mint,
						amount: new BN(quote.inAmount),
						taker: authority,
						slippageLadderBps: SWAP_AND_DEPOSIT_SLIPPAGE_LADDER_BPS,
						onRungFailed: (slippageBps, rungError) =>
							console.warn(
								`Swap simulation failed at ${slippageBps}bps, widening slippage`,
								rungError
							),
					});

				if (!attempt) {
					throw swapBuildError instanceof Error
						? swapBuildError
						: new Error('Could not build a swap at any slippage tolerance');
				}

				const swapTx = attempt.tx;
				// The executed quote, which the ladder may have re-fetched at a wider
				// tolerance than the one on screen. Widening only lowers minOut, so it
				// can't overshoot a destination limit the modal already checked — but
				// it can drop under a minimum the caller depends on.
				minOut = getMinOutBigNum(attempt.quote, usdtMarket.precisionExp);

				if (minAcceptableOut?.gt(minOut)) {
					notify({
						type: 'error',
						message: 'Swap & Deposit unavailable',
						description: `This route can only guarantee ${minOut.prettyPrint()} USDT, below the ${minAcceptableOut.prettyPrint()} USDT minimum. Try a larger amount.`,
						id: TOAST_ID,
						lengthMs: 8000,
					});
					return 'failed';
				}

				notify({
					type: 'awaiting',
					message: `Swapping ${inAmountBigNum.prettyPrint()} USDC`,
					description: `Depositing at least ${minOut.prettyPrint()} USDT`,
					id: TOAST_ID,
					showUntilCancelled: true,
				});

				// ---- Build tx 2: the minOut deposit ----
				const usdtAta = await getTokenAddressForDepositAndWithdraw(
					usdtSpotMarketAccount,
					authority
				);

				const depositIxs: TransactionInstruction[] = [];
				const accountData = targetAccountKey
					? accountsGet().accounts[targetAccountKey]
					: undefined;

				// The new-account branch belongs to an empty key only. A key the
				// store can't resolve must abort, not silently create a subaccount
				// the user never asked for.
				if (targetAccountKey && !accountData) {
					notify({
						type: 'error',
						message: 'Swap & Deposit unavailable',
						description:
							'The selected account is still loading. Please try again.',
						id: TOAST_ID,
						lengthMs: 5000,
					});
					return 'failed';
				}

				/** Set on the brand-new-account path, for post-deposit init handling. */
				let newAccount: { userId: number; isReferred: boolean } | null = null;

				if (accountData) {
					await velocityClient.switchActiveUser(
						accountData.userId,
						accountData.authority
					);
					const depositIx = await velocityClient.getDepositInstruction(
						minOut.val,
						usdtMarket.marketIndex,
						usdtAta,
						accountData.userId,
						false,
						true
					);
					depositIxs.push(depositIx);
				} else {
					// Brand-new (sub)account: initialize it in the same tx.
					const nextUserId = await getUserIdForAccountInitialization(
						velocityClient,
						authority
					);
					if (nextUserId === null) {
						// Unknown id, and everything downstream depends on it. Abort
						// before the swap: signing an initialization for a sub-account
						// that already exists would confirm the swap and then fail the
						// deposit, leaving the user holding USDT they didn't ask for.
						notify({
							type: 'error',
							message: 'Swap & Deposit unavailable',
							description:
								'Could not read your account details. Please try again.',
							id: TOAST_ID,
							lengthMs: 5000,
						});
						return 'failed';
					}

					// getInitializeUserAccountIxs (and getDepositInstruction's default
					// authority) build for the client's `authority`, which is the
					// delegated owner while a delegated account is active. The new
					// account must belong to the connected wallet — the one whose USDT
					// funds it and whose sub-account id we just looked up.
					if (!velocityClient.authority.equals(authority)) {
						await velocityClient.switchActiveUser(nextUserId, authority);
					}

					// referrerParam is only set in the store after on-chain validation
					// (see handleReferralPageLoad), so its presence means a valid referral.
					const referrerInfo = referrerParam
						? await getReferrerInfo(velocityClient, referrerParam)
						: undefined;
					newAccount = { userId: nextUserId, isReferred: !!referrerInfo };

					const [initIxs] = await velocityClient.getInitializeUserAccountIxs(
						nextUserId,
						DEFAULT_ACCOUNT_NAMES_BY_POOL_ID[poolId],
						referrerInfo,
						poolId
					);
					depositIxs.push(...initIxs);

					// Mirror createUserAndDepositCollateralBaseIxs: a referred user
					// needs a RevenueShareEscrow created at signup so referral rewards
					// accrue. initializeRevenueShareEscrow reads userStats.referrer (set
					// by the init ixs above) into the escrow. Only on the first account,
					// where userStats — and its referrer — is created.
					if (referrerInfo && nextUserId === 0) {
						depositIxs.push(
							await velocityClient.getInitializeRevenueShareEscrowIx(
								authority,
								DEFAULT_REFERRAL_ESCROW_NUM_ORDERS
							)
						);
					}

					// getInitializeUserAccountIxs does not create the SignedMsgUserOrders
					// account, unlike the createInitializeUserAccountAndDepositCollateralIxs
					// the regular deposit path uses.
					if (poolId === MAIN_POOL_ID) {
						const { ix: initSwiftAccountIx } =
							await createSwiftAccountIxIfNotExists({
								velocityClient,
								authority,
							});

						if (initSwiftAccountIx) {
							depositIxs.push(initSwiftAccountIx);
						}
					}

					const depositIx = await velocityClient.getDepositInstruction(
						minOut.val,
						usdtMarket.marketIndex,
						usdtAta,
						nextUserId,
						false,
						false
					);
					depositIxs.push(depositIx);
				}

				const depositTx = await velocityClient.buildTransaction(
					depositIxs,
					getTxParams({
						marketId: MarketId.createSpotMarket(usdtMarket.marketIndex),
					})
				);

				// ---- One wallet prompt for both ----
				// walletAdapter is BaseMessageSignerWalletAdapter, which always
				// exposes signAllTransactions (batched by wallets that support it,
				// sequential signTransaction calls otherwise) — no capability
				// check or two-prompt fallback needed.
				const [signedSwapTx, signedDepositTx] =
					await walletAdapter.signAllTransactions([swapTx, depositTx]);

				// Record the signature before sending. A send that throws
				// (blockheight exceeded, RPC timeout) can still have landed, and
				// without this the outer catch would report "nothing moved".
				const swapSignature = bs58.encode(signedSwapTx.signatures[0]);
				swapTxSig = swapSignature;

				// ---- Send swap, wait for confirmation ----
				// preSigned: true is required — velocityClient.sendTransaction
				// otherwise re-signs (and re-prompts the wallet) with a fresh
				// blockhash via its own TxSender, discarding the signature just
				// collected above. Every VelocityClient TxSender (WhileValidTxSender,
				// RetryTxSender, BaseTxSender) resolves only once the transaction is
				// confirmed, so no separate confirmTransaction call is needed here.
				await velocityClient.sendTransaction(
					signedSwapTx,
					undefined,
					undefined,
					true
				);

				// ---- Send the pre-signed deposit ----
				let depositTxSig: string;
				try {
					({ txSig: depositTxSig } = await velocityClient.sendTransaction(
						signedDepositTx,
						undefined,
						undefined,
						true
					));
				} catch (depositErr) {
					// The deposit was pre-signed, so the user can't have rejected it
					// here — blockhash expiry or a network flake.
					console.error('Swap confirmed but deposit failed', depositErr);
					return await degradeToSwapOnly(swapSignature);
				}

				notify({
					type: 'success',
					message: 'Swap & Deposit successful',
					description: `Swapped ${inAmountBigNum.prettyPrint()} USDC and deposited ${minOut.prettyPrint()} USDT`,
					id: TOAST_ID,
				});

				if (newAccount) {
					// The swift query is keyed by authority with staleTime: Infinity, so
					// a wallet that already cached "no swift account" — one adding a
					// subaccount rather than its first — keeps showing the enable-swift
					// prompt until reload without this.
					if (poolId === MAIN_POOL_ID) {
						queryClient.invalidateQueries({
							queryKey: isSignedMsgUserOrdersAccountInitializedQueryKey(
								authority.toBase58()
							),
						});
					}

					// The initialization transaction has already landed, so the shared
					// init pipeline runs with a pass-through initialization step — it
					// still owns subscribing the account, switching to it, and the
					// referral cleanup. Never rethrows: the deposit is done, so a
					// subscription hiccup is a "refresh the page" warning, not a
					// failed action.
					try {
						await runAccountInitializationTxAndHandling({
							isReferred: newAccount.isReferred,
							velocityClient,
							userIdToInit: newAccount.userId,
							// Its own toast, not TOAST_ID — that one already reports the
							// final swap result and shouldn't be overwritten by the
							// account-subscription progress.
							toastId: INIT_TOAST_ID,
							runInitializationTransaction: async () => ({
								txSig: depositTxSig,
							}),
							poolId,
							onAccountCreated,
							customMessages: {
								initializingMessage: 'Deposited',
								successMessage: 'New subaccount created.',
							},
						});
					} catch (initErr) {
						console.error(
							'New account initialization handling failed',
							initErr
						);
						notify({
							type: 'warning',
							message: 'Deposited — account still loading',
							description:
								'Your deposit landed but we could not subscribe to your new account. Please refresh the page.',
							lengthMs: 8000,
						});
					}
				} else if (accountData?.client) {
					updateAfterTx(velocityClient, accountData.client);
				}

				captureEvent('swap_and_deposit_completed', {
					swap_tx_sig: swapSignature,
					slippage_bps: attempt.slippageBps,
				});

				return 'deposited';
			} catch (err: any) {
				// A send can throw on a transaction that still landed. Check before
				// telling the user nothing moved — otherwise they get a generic
				// error toast with their USDC already converted.
				if (swapTxSig && (await swapLanded(swapTxSig))) {
					console.error('Swap landed despite a thrown send', err);
					return await degradeToSwapOnly(swapTxSig);
				}

				TransactionErrorHandler.handleError({
					error: err,
					toastId: TOAST_ID,
					wallet: walletAdapter,
				});
				return 'failed';
			} finally {
				setIsSubmitting(false);
			}
		},
		[
			velocityClient,
			connection,
			walletAdapter,
			isGeoblocked,
			countryCode,
			referrerParam,
			accountsGet,
			setDepositInputStore,
			getTxParams,
			queryClient,
			captureEvent,
			syncTokenAccountBalance,
			runAccountInitializationTxAndHandling,
		]
	);

	return { swapAndDeposit, isSubmitting };
};
